import React from 'react';
import type { PressableProps } from 'react-native';
import { Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';
import { useSwitch } from '@react-native-aria/switch';
import { COLORS } from '@garnish/constants';

import { useStyleSwitch } from '../../hooks/useStyleSwitch';
import { Icon } from '../Icon';
import {
  type StateTypes,
  type SwitchPalette,
  type SwitchProps,
  SwitchState,
} from './Switch.types';

// ─────────────────────────────────────────────────────────────────────

export const sizeStylesConfigs = {
  large: {
    width: 64,
    height: 32,
    borderRadius: 16,
  },
  medium: {
    width: 56,
    height: 32,
    borderRadius: 16,
  },
  small: {
    width: 48,
    height: 24,
    borderRadius: 12,
  },
};

export const thumbStylesConfigs = {
  large: {
    width: 28,
    height: 28,
  },
  medium: {
    width: 28,
    height: 28,
  },
  small: {
    width: 20,
    height: 20,
  },
};
export const iconsStylesConfigs = {
  large: {
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  medium: {},
  small: {
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
};

// ─────────────────────────────────────────────────────────────────────────────

export const Switch = (props: SwitchProps & PressableProps) => {
  const {
    value,
    onChange,
    disabled = false,
    showIcons = true,
    size = 'small',
    accessibilityLabel = 'Garnish Switch',
    accessibilityHint = 'Garnish Switch',
    testID,
    palette = 'kale',
  } = props;

  const { width: switchWidth } = sizeStylesConfigs[size] || {};
  const { width: thumbWidth } = thumbStylesConfigs[size] ?? {};

  const paletteStyles = PALETTE[palette];

  const transitionWidth = switchWidth - thumbWidth - 2 * THUMB_INITIAL_OFFSET;

  const useSizeStyles = useStyleSwitch<StateTypes>('size', sizeStylesConfigs);
  const useThumbStyles = useStyleSwitch<StateTypes>('size', thumbStylesConfigs);
  const useIconStyles = useStyleSwitch<StateTypes>('size', iconsStylesConfigs);

  const inputRef = React.useRef(null);
  const { inputProps } = useSwitch(
    {
      'aria-label': accessibilityLabel,
      accessibilityLabel,
      accessibilityHint,
    },
    {
      toggle() {}, // @REVIEW
      isSelected: value,
      setSelected: onChange,
    },
    inputRef,
  );

  const handleChange = React.useCallback(() => {
    onChange(!value);
  }, [onChange, value]);

  const iconStyles = [styles.iconGroup, useIconStyles(size)];

  const iconColor = disabled ? COLORS.OPACITY.DARK_KALE.LIGHT : COLORS.WHITE;

  // ─── Reanimated Shared Values ────────────────────────────────────────

  const currentState = useDerivedValue(() => {
    if (disabled) {
      return SwitchState.Disabled;
    }

    return withTiming(value ? SwitchState.On : SwitchState.Off, {
      duration: ANIMATION_DURATION_MILLISECONDS,
    });
  });

  const thumbStyle = useAnimatedStyle(() => {
    const translateX = withTiming(value ? transitionWidth : 0, {
      duration: ANIMATION_DURATION_MILLISECONDS,
    });

    return {
      transform: [{ translateX }],
    };
  });

  const thumbColorStyle = useAnimatedStyle(() => {
    const thumbColor = interpolateColor(
      currentState.value,
      [SwitchState.Off, SwitchState.Disabled, SwitchState.On],
      [COLORS.WHITE, COLORS.OPACITY.DARK_KALE.LIGHT, COLORS.WHITE],
    );

    return {
      backgroundColor: thumbColor,
    };
  });

  const backgroundColorStyle = useAnimatedStyle(() => {
    const bgColor = interpolateColor(
      currentState.value,
      [SwitchState.Off, SwitchState.Disabled, SwitchState.On],
      [
        paletteStyles.off.backgroundColor,
        paletteStyles.disabled.backgroundColor,
        paletteStyles.on.backgroundColor,
      ],
    );

    return {
      backgroundColor: bgColor,
    };
  });

  // ─────────────────────────────────────────────────────────────────────

  const animatedPressableStyles = [
    styles.switchPosition,
    backgroundColorStyle,
    useSizeStyles(size),
  ];

  const animatedThumbColorStyle = [
    styles.thumb,
    useThumbStyles(size),
    thumbColorStyle,
  ];

  const animatedThumbPositionStyle = [styles.thumbPosition, thumbStyle];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      {/* ===== Pressable Switch component ===== */}
      <Pressable
        {...inputProps}
        testID={testID}
        disabled={disabled}
        onPress={handleChange}
        accessibilityRole="switch"
        accessibilityLabel={accessibilityLabel}
        accessibilityChecked={value}
      >
        <Animated.View
          style={animatedPressableStyles}
          testID="sg-switch-container"
        >
          {/* ===== Switch Thumb component ===== */}
          <Animated.View style={animatedThumbPositionStyle}>
            <View style={animatedThumbColorStyle} />
          </Animated.View>

          {/* ===== Switch Icon Group component ===== */}
          {showIcons ? (
            <View style={iconStyles}>
              <Icon name="IconCheck" color={iconColor} width={16} height={16} />
              <Icon name="IconClose" color={iconColor} width={16} height={16} />
            </View>
          ) : null}
        </Animated.View>
      </Pressable>
    </View>
  );
};

// ─── Spacer ──────────────────────────────────────────────────────────

export const Spacer = ({ space }: Readonly<{ space: number }>) => {
  return <View style={{ width: space }} />;
};

// ─── Constants ───────────────────────────────────────────────────────────

const THUMB_INITIAL_OFFSET = 2;
const ANIMATION_DURATION_MILLISECONDS = 250;

// ─── Styles ──────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  iconGroup: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  switchPosition: {
    position: 'relative',
  },
  thumbPosition: {
    position: 'absolute',
    top: 2,
    left: THUMB_INITIAL_OFFSET,
    zIndex: 1,
  },
  thumb: {
    backgroundColor: COLORS.WHITE,
    borderRadius: 33,
  },
});

// ─── Palettes ────────────────────────────────────────────────────────────────

type SwitchStateUnion = 'on' | 'off' | 'disabled';

const kalePalette = StyleSheet.create<Record<SwitchStateUnion, PaletteStyle>>({
  on: { backgroundColor: COLORS.KALE },
  off: { backgroundColor: COLORS.LIGHT },
  disabled: { backgroundColor: COLORS.OPACITY.DARK_KALE.LIGHTER },
});

const spinachPalette = StyleSheet.create<
  Record<SwitchStateUnion, PaletteStyle>
>({
  on: { backgroundColor: COLORS.SPINACH },
  off: { backgroundColor: COLORS.LIGHT },
  disabled: { backgroundColor: COLORS.OPACITY.DARK_KALE.LIGHTER },
});

const successPalette = StyleSheet.create<
  Record<SwitchStateUnion, PaletteStyle>
>({
  on: { backgroundColor: COLORS.SUCCESS },
  off: { backgroundColor: COLORS.LIGHT },
  disabled: { backgroundColor: COLORS.OPACITY.DARK_KALE.LIGHTER },
});

const PALETTE: Record<SwitchPalette, PaletteOptions> = {
  kale: kalePalette,
  spinach: spinachPalette,
  success: successPalette,
};

// ─── Types ───────────────────────────────────────────────────────────

type PaletteOptions = {
  on: PaletteStyle;
  off: PaletteStyle;
  disabled: PaletteStyle;
};

type PaletteStyle = {
  backgroundColor: string;
};
